import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { TabView, TabPanel } from 'primereact/tabview';
import api from '../../api/axios';

export default function ProductDetailPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Date Filter States
  const [dates, setDates] = useState(() => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    return [start, end];
  });

  // Sales Stock Filtering States
  const [showDropFromStock, setShowDropFromStock] = useState(true);
  const [showNonDropFromStock, setShowNonDropFromStock] = useState(true);

  const fetchProductDetail = async () => {
    try {
      setLoading(true);
      let url = `product/get-product/${id}`;

      if (dates && dates[0] && dates[1]) {
        const start = dates[0].toISOString();
        const end = dates[1].toISOString();
        url += `?startDate=${start}&endDate=${end}`;
      }

      const res = await api.get(url);
      setProduct(res.data.data);
    } catch (err) {
      toast.current.show({
        severity: 'error',
        summary: 'Hata',
        detail: 'Ürün detayları yüklenemedi.',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductDetail();
  }, [id]);

  const handleSearch = () => {
    fetchProductDetail();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <i className="pi pi-spin pi-spinner text-4xl text-blue-600"></i>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <Card>Ürün bulunamadı.</Card>
      </div>
    );
  }

  const filteredSales = product.sales?.filter(sale => {
    const isDrop = sale.dropFromStock !== false;
    if (isDrop && !showDropFromStock) return false;
    if (!isDrop && !showNonDropFromStock) return false;
    return true;
  }) || [];

  return (
    <div className="p-4 space-y-6 animate-fade-in">
      <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

      {/* Üst Kısım: Ürün Bilgi Kartı */}
      <Card
        title={
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="!bg-blue-600 p-4 rounded-xl shadow-md">
                  <i className="pi pi-box text-white text-xl"></i>
                </div>
                <div>
                  <h1 className="text-xl font-black text-slate-900 uppercase">{product.name}</h1>
                  <p className="text-xs text-slate-500 font-bold">{product.brandName || 'Markasız'} | {product.barcode || 'Barkodsuz'}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                    label="Düzenle"
                    icon="pi pi-pencil"
                    className="p-button-outlined p-button-warning rounded-xl"
                    onClick={() => navigate(`/products/edit/${id}`)}
                />
                <Button
                    label="Geri"
                    icon="pi pi-arrow-left"
                    className="p-button-outlined p-button-secondary rounded-xl"
                    onClick={() => navigate('/products')}
                />
              </div>
            </div>

            {/* Filtre Alanı */}
            <div className="!bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-col md:flex-row items-end gap-3">
              <div className="flex-1 w-full">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">TARİH ARALIĞI</label>
                <Calendar
                  value={dates}
                  onChange={(e) => setDates(e.value)}
                  selectionMode="range"
                  readOnlyInput
                  hideOnRangeSelection
                  showIcon
                  className="w-full"
                  inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                  placeholder="Tarih aralığı seçin..."
                  dateFormat="dd.mm.yy"
                  locale="tr"
                />
              </div>
              <Button
                label="ARA"
                icon="pi pi-search"
                className="!bg-slate-900 border-none px-8 py-2.5 rounded-xl font-black text-xs h-[42px] transition-all active:scale-95"
                onClick={handleSearch}
                loading={loading}
              />
            </div>
          </div>
        }
        className="shadow-lg border-none rounded-2xl"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-2">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">SON ALIŞ FİYATI</div>
            <div className="text-xl font-black text-slate-800">
              {product.lastPurchase?.unitPrice?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
            </div>
            <div className="text-[10px] text-slate-500 font-medium mt-1">
              {product.lastPurchase?.supplierName || 'Kayıt yok'}
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
            <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">TOPLAM ALIŞ MİKTARI</div>
            <div className="text-xl font-black text-blue-800">
              {product.totalPurchaseQuantity?.toLocaleString('tr-TR')} {product.unitType}
            </div>
          </div>

          <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-100">
            <div className="text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1">TOPLAM SATIŞ MİKTARI</div>
            <div className="text-xl font-black text-emerald-800">
              {product.totalSaleQuantity?.toLocaleString('tr-TR')} {product.unitType}
            </div>
          </div>

          <div className={`p-4 rounded-xl border ${product.currentStockQuantity <= product.criticalStockQuantity ? 'bg-red-50 border-red-100' : 'bg-slate-900 border-slate-800 text-white'}`}>
            <div className={`text-[10px] font-black uppercase tracking-widest mb-1 ${product.currentStockQuantity <= product.criticalStockQuantity ? 'text-red-400' : 'text-slate-400'}`}>MEVCUT STOK MİKTARI</div>
            <div className={`text-xl font-black ${product.currentStockQuantity <= product.criticalStockQuantity ? 'text-red-800' : 'text-white'}`}>
              {product.currentStockQuantity?.toLocaleString('tr-TR')} {product.unitType}
            </div>
            {product.currentStockQuantity <= product.criticalStockQuantity && (
              <div className="text-[10px] text-red-600 font-bold mt-1 uppercase">KRİTİK STOK SEVİYESİ!</div>
            )}
          </div>

          {product.trackExpirationDate && (
            <div className={`p-4 rounded-xl border ${product.nearestExpirationDate && new Date(product.nearestExpirationDate) < new Date() ? 'bg-red-50 border-red-100' : 'bg-orange-50 border-orange-100'}`}>
              <div className={`text-[10px] font-black uppercase tracking-widest mb-1 ${product.nearestExpirationDate && new Date(product.nearestExpirationDate) < new Date() ? 'text-red-400' : 'text-orange-400'}`}>EN YAKIN SKT</div>
              <div className={`text-xl font-black ${product.nearestExpirationDate && new Date(product.nearestExpirationDate) < new Date() ? 'text-red-800' : 'text-orange-800'}`}>
                {product.nearestExpirationDate ? new Date(product.nearestExpirationDate).toLocaleDateString('tr-TR') : 'Belirtilmedi'}
              </div>
              {product.nearestExpirationDate && new Date(product.nearestExpirationDate) < new Date() && (
                <div className="text-[10px] text-red-600 font-bold mt-1 uppercase">SÜRESİ DOLMUŞ ÜRÜN VAR!</div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Alt Kısım: Tab Görünümü ile Bilgilerin Listelenmesi */}
      <div className="bg-white dark:bg-slate-900 shadow-xl rounded-[2rem] border border-slate-200 dark:border-slate-800 p-6 pb-8">
        <TabView className="custom-tabview">
          {/* Tab 1: Alış Bilgileri */}
          <TabPanel 
            header={
              <div className="flex items-center gap-2">
                <i className="pi pi-shopping-cart text-blue-600 text-sm" />
                <span className="font-black text-xs uppercase tracking-wider">Alış Bilgileri</span>
                <Tag value={`${product.purchases?.length || 0}`} className="bg-blue-600 text-[9px] font-black px-1.5 rounded-full" />
              </div>
            }
          >
            <div className="mt-4">
              <DataTable
                value={product.purchases}
                rows={10}
                paginator
                responsiveLayout="scroll"
                className="p-datatable-sm"
                emptyMessage="Alış kaydı bulunamadı."
              >
                <Column field="supplierName" header="TEDARİKÇİ" sortable className="font-bold text-xs" />
                <Column
                  field="quantity"
                  header="MİKTAR"
                  sortable
                  body={(row) => (
                    <span className="font-black text-slate-700 dark:text-slate-350">
                      {row.quantity} {row.unit}
                    </span>
                  )}
                />
                <Column
                  field="unitPrice"
                  header="ALIŞ FİYATI"
                  sortable
                  body={(row) => (
                    <span className="font-black text-blue-600">
                      {row.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </span>
                  )}
                />
                <Column
                  header="PARTİ / SKT"
                  body={(row) => (
                    <div className="flex flex-col text-[10px]">
                      <span className="font-bold text-slate-700 dark:text-slate-350">{row.batchNo || '-'}</span>
                      <span className="text-slate-400">{row.expirationDate ? new Date(row.expirationDate).toLocaleDateString('tr-TR') : '-'}</span>
                    </div>
                  )}
                />
                <Column
                  field="purchaseDate"
                  header="TARİH"
                  sortable
                  body={(row) => (
                    <span className="text-slate-500 font-medium text-[11px]">
                      {new Date(row.purchaseDate).toLocaleDateString('tr-TR')}
                    </span>
                  )}
                />
              </DataTable>
            </div>
          </TabPanel>

          {/* Tab 2: Satış Bilgileri */}
          <TabPanel 
            header={
              <div className="flex items-center gap-2">
                <i className="pi pi-shopping-bag text-emerald-600 text-sm" />
                <span className="font-black text-xs uppercase tracking-wider">Satış Bilgileri</span>
                <Tag value={`${filteredSales?.length || 0}`} className="bg-emerald-600 text-[9px] font-black px-1.5 rounded-full" />
              </div>
            }
          >
            <div className="mt-4 space-y-4">
              <div className="flex items-center flex-wrap gap-4 bg-slate-50 dark:bg-slate-950 p-4 rounded-2xl border border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={showDropFromStock}
                    onChange={(e) => setShowDropFromStock(e.checked)}
                    inputId="showDropFromStock"
                  />
                  <label htmlFor="showDropFromStock" className="text-[10px] font-black text-slate-600 dark:text-slate-400 uppercase cursor-pointer select-none">Stoktan Düşenler</label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={showNonDropFromStock}
                    onChange={(e) => setShowNonDropFromStock(e.checked)}
                    inputId="showNonDropFromStock"
                  />
                  <label htmlFor="showNonDropFromStock" className="text-[10px] font-black text-slate-600 dark:text-slate-400 uppercase cursor-pointer select-none">Stoktan Düşmeyenler</label>
                </div>
              </div>
              <DataTable
                value={filteredSales}
                rows={10}
                paginator
                responsiveLayout="scroll"
                className="p-datatable-sm"
                emptyMessage="Satış kaydı bulunamadı."
              >
                <Column
                  field="saleDate"
                  header="TARİH"
                  sortable
                  body={(row) => (
                    <span className="text-slate-500 font-medium text-[11px]">
                      {new Date(row.saleDate).toLocaleDateString('tr-TR')}
                    </span>
                  )}
                />
                <Column field="customerName" header="MÜŞTERİ" sortable className="font-bold text-xs" />
                <Column
                  field="quantity"
                  header="ADET"
                  sortable
                  body={(row) => (
                    <span className="font-black text-slate-700 dark:text-slate-350">{row.quantity}</span>
                  )}
                />
                <Column
                  field="dropFromStock"
                  header="STOK"
                  sortable
                  className="text-center"
                  headerClassName="text-center"
                  style={{ width: '80px' }}
                  body={(row) => (
                    <i className={`pi ${row.dropFromStock !== false ? 'pi-check-circle text-emerald-500' : 'pi-times-circle text-red-400'} text-base`}></i>
                  )}
                />
                <Column
                  field="unitPrice"
                  header="BİRİM FİYAT"
                  sortable
                  body={(row) => (
                    <span className="font-black text-slate-600 dark:text-slate-400">
                      {row.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </span>
                  )}
                />
                <Column
                  field="totalAmount"
                  header="TOPLAM"
                  sortable
                  body={(row) => (
                    <span className="font-black text-emerald-600">
                      {row.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </span>
                  )}
                />
                <Column
                  header="PARTİ / SKT"
                  body={(row) => (
                    <div className="flex flex-col text-[10px]">
                      <span className="font-bold text-slate-700 dark:text-slate-350">{row.batchNo || '-'}</span>
                      <span className="text-slate-400">{row.expirationDate ? new Date(row.expirationDate).toLocaleDateString('tr-TR') : '-'}</span>
                    </div>
                  )}
                />
              </DataTable>
            </div>
          </TabPanel>

          {/* Tab 3: Tedarikçiye İadeler */}
          <TabPanel 
            header={
              <div className="flex items-center gap-2">
                <i className="pi pi-refresh text-orange-600 text-sm" />
                <span className="font-black text-xs uppercase tracking-wider">Tedarikçiye İadeler</span>
                <Tag value={`${product.purchasesReturns?.length || 0}`} className="bg-orange-600 text-[9px] font-black px-1.5 rounded-full" />
              </div>
            }
          >
            <div className="mt-4">
              <DataTable
                value={product.purchasesReturns}
                rows={10}
                paginator
                responsiveLayout="scroll"
                className="p-datatable-sm"
                emptyMessage="İade kaydı bulunamadı."
              >
                <Column field="supplierName" header="TEDARİKÇİ" sortable className="font-bold text-xs" />
                <Column
                  field="quantity"
                  header="MİKTAR"
                  sortable
                  body={(row) => (
                    <span className="font-black text-slate-700 dark:text-slate-350">
                      {row.quantity} {row.unit}
                    </span>
                  )}
                />
                <Column
                  field="unitPrice"
                  header="ALIŞ FİYATI"
                  sortable
                  body={(row) => (
                    <span className="font-black text-orange-600">
                      {row.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </span>
                  )}
                />
                <Column
                  field="returnDate"
                  header="TARİH"
                  sortable
                  body={(row) => (
                    <span className="text-slate-500 font-medium text-[11px]">
                      {new Date(row.returnDate).toLocaleDateString('tr-TR')}
                    </span>
                  )}
                />
                <Column
                  header="PARTİ / SKT"
                  body={(row) => (
                    <div className="flex flex-col text-[10px]">
                      <span className="font-bold text-slate-700 dark:text-slate-350">{row.batchNo || '-'}</span>
                      <span className="text-slate-400">{row.expirationDate ? new Date(row.expirationDate).toLocaleDateString('tr-TR') : '-'}</span>
                    </div>
                  )}
                />
              </DataTable>
            </div>
          </TabPanel>

          {/* Tab 4: Müşteriden İadeler */}
          <TabPanel 
            header={
              <div className="flex items-center gap-2">
                <i className="pi pi-refresh text-red-600 text-sm" />
                <span className="font-black text-xs uppercase tracking-wider">Müşteriden İadeler</span>
                <Tag value={`${product.salesReturns?.length || 0}`} className="bg-red-600 text-[9px] font-black px-1.5 rounded-full" />
              </div>
            }
          >
            <div className="mt-4">
              <DataTable
                value={product.salesReturns}
                rows={10}
                paginator
                responsiveLayout="scroll"
                className="p-datatable-sm"
                emptyMessage="İade kaydı bulunamadı."
              >
                <Column
                  field="returnDate"
                  header="TARİH"
                  sortable
                  body={(row) => (
                    <span className="text-slate-500 font-medium text-[11px]">
                      {new Date(row.returnDate).toLocaleDateString('tr-TR')}
                    </span>
                  )}
                />
                <Column field="customerName" header="MÜŞTERİ" sortable className="font-bold text-xs" />
                <Column
                  field="quantity"
                  header="ADET"
                  sortable
                  body={(row) => (
                    <span className="font-black text-slate-700 dark:text-slate-350">{row.quantity}</span>
                  )}
                />
                <Column
                  field="totalAmount"
                  header="TOPLAM"
                  sortable
                  body={(row) => (
                    <span className="font-black text-red-600">
                      {row.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    </span>
                  )}
                />
                <Column
                  header="PARTİ / SKT"
                  body={(row) => (
                    <div className="flex flex-col text-[10px]">
                      <span className="font-bold text-slate-700 dark:text-slate-350">{row.batchNo || '-'}</span>
                      <span className="text-slate-400">{row.expirationDate ? new Date(row.expirationDate).toLocaleDateString('tr-TR') : '-'}</span>
                    </div>
                  )}
                />
              </DataTable>
            </div>
          </TabPanel>
        </TabView>
      </div>

      <style>{`
        /* Custom TabView styling */
        .custom-tabview .p-tabview-nav {
            background: transparent !important;
            border-bottom: 2px solid #f1f5f9 !important;
            display: flex;
            gap: 1rem;
            margin-bottom: 1.5rem;
        }
        .dark .custom-tabview .p-tabview-nav {
            border-bottom-color: #1e293b !important;
        }
        .custom-tabview .p-tabview-nav li {
            margin-right: 0 !important;
        }
        .custom-tabview .p-tabview-nav li .p-tabview-nav-link {
            background: transparent !important;
            border: none !important;
            border-bottom: 3px solid transparent !important;
            color: #64748b !important;
            font-weight: 900 !important;
            padding: 1rem 0.5rem !important;
            transition: all 0.2s !important;
            margin-bottom: -2px !important;
        }
        .dark .custom-tabview .p-tabview-nav li .p-tabview-nav-link {
            color: #94a3b8 !important;
        }
        .custom-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
            border-bottom-color: #4f46e5 !important;
            color: #4f46e5 !important;
        }
        .dark .custom-tabview .p-tabview-nav li.p-highlight .p-tabview-nav-link {
            color: #818cf8 !important;
            border-bottom-color: #818cf8 !important;
        }
        .custom-tabview .p-tabview-panels {
            background: transparent !important;
            padding: 0 !important;
        }
      `}</style>
    </div>
  );
}
